import { LegalText, PrivacyCheckboxes, apiClient } from '@/api';
import { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useSetRecoilState } from 'recoil';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';

import { termsAcceptedSignalState } from '@/state/project';

const ACK_LABEL = 'I have read and agree to the Privacy Policy.';

type Props =
  | { mode: 'accept'; onAccepted: () => void }
  | { mode: 'read'; onClose: () => void };

export default function PrivacyPolicyModal(props: Props) {
  const isAccept = props.mode === 'accept';
  const [policy, setPolicy] = useState<LegalText | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const bumpTermsAccepted = useSetRecoilState(termsAcceptedSignalState);

  useEffect(() => {
    let cancelled = false;
    apiClient
      .getPrivacyText()
      .then((p) => {
        if (!cancelled) setPolicy(p);
      })
      .catch((err: Error) => {
        if (!cancelled)
          setLoadError(err.message || 'Failed to load privacy policy');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleAccept = async () => {
    if (!isAccept || !acknowledged || submitting) return;
    setSubmitting(true);
    try {
      const checkboxes: PrivacyCheckboxes = { privacy_policy: true };
      const result = await apiClient.acceptPrivacy(checkboxes);
      if (result.accepted) {
        bumpTermsAccepted((n) => n + 1);
        props.onAccepted();
      } else {
        toast.error('Acceptance was not recorded — please try again.');
      }
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Failed to record acceptance'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open && !isAccept) props.onClose();
  };

  return (
    <Dialog open onOpenChange={handleOpenChange}>
      <DialogContent
        className={
          'max-w-3xl max-h-[90vh] flex flex-col gap-0 p-0' +
          (isAccept ? ' [&>button]:hidden' : '')
        }
        onInteractOutside={(e) => {
          if (isAccept) e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          if (isAccept) e.preventDefault();
        }}
      >
        <DialogHeader className="p-6 pb-3 border-b">
          <DialogTitle>Crimson Raven — Privacy Policy</DialogTitle>
          <DialogDescription>
            {isAccept
              ? 'Read the privacy policy and tick the box to continue. This is shown again only when its content is updated.'
              : 'How Crimson Raven collects, uses, and protects your data.'}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4 text-sm leading-relaxed [&_h1]:text-lg [&_h1]:font-semibold [&_h1]:mt-4 [&_h1]:mb-2 [&_h2]:text-base [&_h2]:font-semibold [&_h2]:mt-4 [&_h2]:mb-2 [&_h3]:font-semibold [&_h3]:mt-3 [&_h3]:mb-1 [&_p]:mb-2 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-2 [&_li]:mb-1 [&_table]:w-full [&_table]:border-collapse [&_th]:border [&_th]:px-2 [&_th]:py-1 [&_th]:text-left [&_td]:border [&_td]:px-2 [&_td]:py-1">
          {!policy && !loadError && (
            <div className="text-muted-foreground">Loading privacy policy…</div>
          )}
          {loadError && (
            <div className="text-destructive">
              Failed to load privacy policy: {loadError}
            </div>
          )}
          {policy && (
            <section data-testid="privacy-section">
              <ReactMarkdown>{policy.text}</ReactMarkdown>
            </section>
          )}
        </div>

        <div className="border-t p-6 space-y-3">
          {isAccept ? (
            <>
              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox
                  checked={acknowledged}
                  onCheckedChange={(v) => setAcknowledged(Boolean(v))}
                  aria-label={ACK_LABEL}
                  data-testid="checkbox-privacy_policy"
                />
                <span className="text-sm leading-snug">{ACK_LABEL}</span>
              </label>
              <div className="flex justify-end pt-2">
                <Button
                  onClick={handleAccept}
                  disabled={!acknowledged || submitting}
                  data-testid="accept-button"
                >
                  {submitting ? 'Recording…' : 'Accept and Continue'}
                </Button>
              </div>
            </>
          ) : (
            <div className="flex justify-end">
              <Button
                variant="outline"
                onClick={() => props.onClose()}
                data-testid="close-button"
              >
                Close
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
